import app from './app';

import History from './utils/History';
import Pane from './utils/Pane';
import DiscussionPage from './components/DiscussionPage';
import SignUpModal from './components/SignUpModal';
import HeaderPrimary from './components/HeaderPrimary';
import HeaderTitle from './dianLiang/HeaderTitle';
import HeaderSecondary from './components/HeaderSecondary';
import Composer from './components/Composer';
import DiscussionRenamedNotification from './components/DiscussionRenamedNotification';
import CommentPost from './components/CommentPost';
import DiscussionRenamedPost from './components/DiscussionRenamedPost';
import routes, { ForumRoutes, makeRouteHelpers } from './routes';
import alertEmailConfirmation from './utils/alertEmailConfirmation';
import Application, { ApplicationData } from '../common/Application';
import Navigation from '../common/components/Navigation';
import NotificationListState from './states/NotificationListState';
import GlobalSearchState from './states/GlobalSearchState';
import DiscussionListState from './states/DiscussionListState';
import ComposerState from './states/ComposerState';
import isSafariMobile from './utils/isSafariMobile';

import type Notification from './components/Notification';
import type Post from './components/Post';
import type Discussion from '../common/models/Discussion';
import type NotificationModel from '../common/models/Notification';
import type PostModel from '../common/models/Post';
import extractText from '../common/utils/extractText';
import PkgLogo from '../common/models/PkgLogo';
import AlertUtil from '../forum/dianLiang/alertUtil';
import SignInModal from '../forum/dianLiang/SignInModal';
import PublicizeModal from '../forum/dianLiang/PublicizeModal';
import BindRoleModal from '../forum/dianLiang/BindRoleModal';
// import LogInModal from '../forum/components/LogInModal';
import LogInModal from './components/LogInModal';
// import { getSDK, CbEvents } from '@openim/wasm-client-sdk';

export interface ForumApplicationData extends ApplicationData {}

export default class ForumApplication extends Application {
  /**
   * A map of notification types to their components.
   */
  notificationComponents: Record<string, ComponentClass<{ notification: NotificationModel }, Notification<{ notification: NotificationModel }>>> = {
    discussionRenamed: DiscussionRenamedNotification,
  };

  /**
   * A map of post types to their components.
   */
  postComponents: Record<string, ComponentClass<{ post: PostModel }, Post<{ post: PostModel }>>> = {
    comment: CommentPost,
    discussionRenamed: DiscussionRenamedPost,
  };

  /**
   * An object which controls the state of the page's side pane.
   */
  pane: Pane | null = null;

  /**
   * The app's history stack, which keeps track of which routes the user visits
   * so that they can easily navigate back to the previous route.
   */
  history: History = new History();

  /**
   * An object which controls the state of the user's notifications.
   */
  notifications: NotificationListState = new NotificationListState();

  /**
   * An object which stores previously searched queries and provides convenient
   * tools for retrieving and managing search values.
   */
  search: GlobalSearchState = new GlobalSearchState();

  /**
   * An object which controls the state of the composer.
   */
  composer: ComposerState = new ComposerState();

  /**
   * An object which controls the state of the cached discussion list, which
   * is used in the index page and the slideout pane.
   */
  discussions: DiscussionListState = new DiscussionListState({});

  route: typeof Application.prototype.route & ForumRoutes;

  data!: ForumApplicationData;

  constructor() {
    super();

    routes(this);

    this.route = Object.assign((Object.getPrototypeOf(Object.getPrototypeOf(this)) as Application).route.bind(this), makeRouteHelpers(this));
  }

  
  /**
   * @inheritdoc
   */
  mount() {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const LoginStatus = urlParams.get('login');
  //     console.log("LoginStatus",LoginStatus);
  //     if (LoginStatus) {
  //      const { history } = app;
  //  const previous = history?.getPrevious();
  //      console.log("route",history,previous,document.referrer);
       
  //      app.modal.show(LogInModal, { retrunUrl: document.referrer });
  //     }
    // Get the configured default route and update that route's path to be '/'.
    // Push the homepage as the first route, so that the user will always be
    // able to click on the 'back' button to go home, regardless of which page
    // they started on.
    const defaultRoute = this.forum.attribute('defaultRoute');
    let defaultAction = 'index';

    for (const i in this.routes) {
      if (this.routes[i].path === defaultRoute) defaultAction = i;
    }
    this.routes[defaultAction].path = '/';
    this.history.push(defaultAction, extractText(this.translator.trans('core.forum.header.back_to_index_tooltip')), '/');

    this.pane = new Pane(document.getElementById('app'));

    m.route.prefix = '';
    super.mount(this.forum.attribute('basePath'));

    // We mount navigation and header components after the page, so components
    // like the back button can access the updated state when rendering.
    m.mount(document.getElementById('app-navigation')!, { view: () => <Navigation className="App-backControl" drawer /> });
    m.mount(document.getElementById('header-navigation')!, Navigation);
    m.mount(document.getElementById('header-title')!, HeaderTitle);
    m.mount(document.getElementById('header-primary')!, HeaderPrimary);
    m.mount(document.getElementById('header-secondary')!, HeaderSecondary);
    m.mount(document.getElementById('composer')!, { view: () => <Composer state={this.composer} /> });

      this.checkRedirectInfo();
  
    alertEmailConfirmation(this);
    if (app.session.user) {
      // 查询平台币余额
      app
        .request({
          method: 'GET',
          url: app.forum.attribute('apiUrl') + '/sdkUserPc',
        })
        .then((res) => {
          this.balance = res.balance;
          app.store.balance = this.balance;
          m.redraw();
        });

      AlertUtil.getRoleInfo(app);

      const userId = app.session.user?.id();
      app
        .request({
          method: 'GET',
          url: app.forum.attribute('apiUrl') + '/userLevel/saveSignInReward',
          params: {
            userId: userId,
          },
        })
        .catch((e) => console.error(e))
        .then((results) => {
          let userConfig = {
            userName: app.session.user?.username(),
          };
          let loginConfig = {
            userId: app.session.user?.uniqueID(),
            userConfig: userConfig,
            isEnterpriseAuth: false,
          };
          if (results && results.canCheckin) {
            app.modal.show(SignInModal, { results: results }, true);
          }
          let feedbackStatus = app.session.user && app.forum.attribute('feedbackStatus');
          if (feedbackStatus) {
            AIHelpSupport.login(loginConfig);
            let locale = app.data.locale;
            if (app.data.locale == 'zh-Hant') {
              locale = 'zh-TW';
            } else if (app.data.locale == 'zh-Hans') {
              locale = 'zh-CN';
            }
            AIHelpSupport.updateSDKLanguage(locale);
          }


          // console.log('puh');

        });

        //猜谜活动是否开启
        if (app.forum.attribute('guessGameStatus')) {
          // app.store.find('guessGame').then((res) => {
          //   app.data['GuessGameRole'] = res;
          //   console.log('猜谜活动开启res->', res);

          //   m.redraw();
          // });
          app.request({
            method: 'GET',
            url: app.forum.attribute('apiUrl') + '/guessGame',
          }).then((res)=>{
            if (res && res?.data) {
              app.data['GuessGameRole'] = res?.data;
            }
          })
        }
    }

    app.store.find('pkgActivity').then((results) => {
      this.loading = false;
      // Redraw
      m.redraw();
    });

    //  app.store.find('tags').then(results => {
    //   this.loading = false;
    //   // Redraw
    //   m.redraw();
    // });

    let centerIconList = [];
    app.store
      .find('centerIconPkgIdList', {
        sort: 'sortcount',
      })
      .then((results) => {
        [].push.apply(centerIconList, results);
        // this.loading = false;
        // m.redraw();
      });

    const params = new URLSearchParams(window.location.search);
    const bindRole = params.get('bindRole');
    if (bindRole == '0' && app.session.user) {
      // 没有绑定角色 是从sdk登录进来的
      app.modal.show(BindRoleModal);
    }

    // app.store.find('headSlideShow'
    // // ,{ filter: { pkgId: app.session.pkgLogo.id() } }
    // )
    // .then((res) => {
    //   // app.store
    //   //   .all('headSlideShowCategories')
    //   //   .filter((headSlideShow) => (headSlideShow.pkgId = app.data.pkgLogo.id))
    //   //   .sort((a, b) => a.id() - b.id());
    //   // this.loading = false;
    //   m.redraw();
    // });

    // Route the home link back home when clicked. We do not want it to register
    // if the user is opening it in a new tab, however.
    document.getElementById('home-link')!.addEventListener('click', (e) => {
      if (e.ctrlKey || e.metaKey || e.button === 1) return;
      e.preventDefault();
      app.history.home();

      // Reload the current user so that their unread notification count is refreshed.
      const userId = app.session.user?.id();
      if (userId) {
        app.store.find('users', userId);
        m.redraw();
      }
    });

    if (isSafariMobile()) {
      $(() => {
        $('.App').addClass('mobile-safari');
        $('#modal').addClass('mobile-safari');
      });
    }
  }

  checkRedirectInfo() {
      try {
          const stored = localStorage.getItem('redirect_info');
          
          if (stored) {
              // 解析JSON数据
              const redirectInfo = JSON.parse(stored);
              
              // console.log('收到重定向信息:', redirectInfo);
              
              // 处理重定向逻辑
              this.handleRedirectInfo(redirectInfo);
              
              // 清除存储（一次性使用）
              localStorage.removeItem('redirect_info');
          }
      } catch (error) {
          console.error('解析重定向信息失败:', error);
          // 清除可能损坏的数据
          localStorage.removeItem('redirect_info');
      }
  }

  handleRedirectInfo(info) {
    const { errorCode, message, returnUrl, targetUrl } = info;
    
    switch(errorCode) {
        case 401:
            // 显示登录提示
            app.modal.show(LogInModal, { returnUrl: returnUrl, targetUrl: targetUrl });
            break;
        case 403:
            alert(app.translator.trans('dl-optimization.forum.insufficient_permissions') + ': ' + (message || app.translator.trans('dl-optimization.forum.not_access')));
            break;
        case 404:
            alert(app.translator.trans('dl-optimization.forum.not_page') + ': ' + (message || app.translator.trans('dl-optimization.forum.request_page_is_not_exist')));
            break;
        default:
            alert(app.translator.trans('dl-optimization.forum.error') + ': ' + (message || app.translator.trans('dl-optimization.forum.unknown_error')));
    }
  }
  /**
   * Check whether or not the user is currently viewing a discussion.
   */
  public viewingDiscussion(discussion: Discussion): boolean {
    return this.current.matches(DiscussionPage, { discussion });
  }

  /**
   * Callback for when an external authenticator (social login) action has
   * completed.
   *
   * If the payload indicates that the user has been logged in, then the page
   * will be reloaded. Otherwise, a SignUpModal will be opened, prefilled
   * with the provided details.
   */
  public authenticationComplete(payload: Record<string, unknown>): void {
    if (payload.loggedIn) {
      window.location.reload();
    } else {
      this.modal.show(SignUpModal, payload);
    }
  }
}
